import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { userIsAny } from '../../auth/authUtils';
import userTypes from '../../../constants/userTypes';
import { useTranslation } from 'react-i18next';
import { gamesColumnDefinitions } from './GamesColumnDefinitions';
import { useAppSelector } from '../../../hooks';
import { exportGameListData, getGamesWithSku } from '../../../api/gameApi';

export interface GameListWithSkuProps {
  reload: any;
  itemsChecked: any[];
  setItemsChecked: (items: any[]) => void;
  onRequestKeys: (e: any, item: any) => void;
  onSteamBulkRequest: (e: any, items: any[]) => void;
  setBulkActionsSlideoutOpen: (open: boolean) => void;
  tableViewSwitcher: React.ReactNode;
}

function GameListWithSku({
  reload,
  itemsChecked,
  setItemsChecked,
  onRequestKeys,
  onSteamBulkRequest,
  setBulkActionsSlideoutOpen,
  tableViewSwitcher,
}: GameListWithSkuProps) {
  const { t } = useTranslation();
  const currentUser = useAppSelector((state) => state.auth);

  const location = useLocation();
  let allColumns = [
    'gameName',
    'skuName',
    'franchise',
    'publisher',
    'developer',
    'appId',
    'packageId',
    'openRequests',
    'isArchived',
    'platform',
    'steamRef',
    'packageType',
    'canRequestFromDate',
    'canRequestUntilDate',
    'territories',
    'creationDate',
  ];

  let defaultVisibleColumns = [
    'gameName',
    'skuName',
    'franchise',
    'publisher',
    'developer',
    'appId',
    'packageId',
    'openRequests',
    'isArchived',
    'creationDate',
  ];

  if (
    userIsAny(currentUser, [
      userTypes.ADMIN,
      userTypes.APPROVER,
      userTypes.UPLOADER,
    ])
  ) {
    allColumns = [...allColumns, 'requestActivity', 'keyActivity'];
  }

  const suffixColumns = ['buttons-checkbox'];
  const [games, setGames] = useState<any>();
  const params = new URLSearchParams(location.search);

  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'gameListSKU-settings',
    getTableSettingsDefaults({
      defaultSort: 'gameName',
      defaultVisibleColumns,
    }),
    null,
    getGamesWithSku,
    setGames,
    reload,
    t('Failed to load games')
  );
  const handleCheckAllChanged = (checked: boolean) => {
    checked
      ? setItemsChecked(
        games?.data
          ?.filter((x: any) => !x.isArchived)
          .map((x: any) => ({
            gameId: x.gameId,
            gameName: x.gameName,
            skuIds: [x.skuId],
          })) ?? []
      )
      : setItemsChecked([]);
  };
  const handleCheckChanged = (checked: boolean, item: any) =>
    checked
      ? setItemsChecked([
        ...itemsChecked,
        {
          gameId: item.gameId,
          gameName: item.gameName,
          skuIds: [item.skuId],
        },
      ])
      : setItemsChecked(
        itemsChecked.filter(
          (x: any) =>
            x.gameId !== item.gameId ||
            !x.skuIds.some((y: any) => y === item.skuId)
        )
      );
  const isChecked = (item: any) =>
    itemsChecked?.some(
      (x: any) => x.gameId === item.gameId && x.skuIds.some((y: any) => y === item.skuId)
    );

  const columnsConfig = gamesColumnDefinitions({
    currentUser,
    params,
    filterChange,
    games,
    onRequestKeys,
    onSteamBulkRequest,
    handleCheckAllChanged,
    handleCheckChanged,
    isChecked,
  });

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = suffixColumns
    .map((x) => columnsConfig.find((y: any) => y.Key === x))
    .filter((x) => x);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  // return an alaska table
  return games ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        className="gamesTable"
        rows={games.data.map((x: any) => {
          return { Key: x.skuId, ...x };
        })}
        totalRowCount={games.totalRowCount}
        filteredRowCount={games.filteredRowCount}
        scopes={games.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={(filters, scopes) =>
          exportGameListData({
            filters: (filters ?? {}) as Record<string, string>,
            scopes: (scopes ?? {}) as Record<string, string>,
          })
        }
        setBulkActionsSlideoutOpen={setBulkActionsSlideoutOpen}
        itemsChecked={itemsChecked}
        bulkActionTitle={
          itemsChecked.length === 0
            ? t('Bulk actions - first tick some SKUs')
            : t('Bulk actions')
        }
        tableActionBarButtons={tableViewSwitcher}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default GameListWithSku;
